import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import "./UserData.css";
import React, { useMemo, useRef, useState } from "react";
import { headerConfig } from "../../../axiosconfig";
import axios from "axios";
import { useCookies } from "react-cookie";
import { Header } from "../../chatMain/components/head";
import { toast } from "react-toastify";
import countries from "world-countries";
import ReactCountryFlag from "react-country-flag";
import { Box, MenuItem, TextField } from "@mui/material";
import {
  buildCountryList,
  normalizePearliAge,
  normalizePearliDescription,
  normalizePearliGender,
} from "../../../utils/pearliUtils";

const API_URL = process.env.REACT_APP_API_URL;

const handleClick = async (
  cookies,
  navigate,
  setIsLoading,
  firstName,
  lastName,
  age,
  gender,
  location,
  email,
  company,
  phone,
  jobTitle,
  interest,
  referFriend,
  role
) => {
  try {
    setIsLoading(true);

    // Normalize values
    let ageSelection = age === "-" ? "prefer not to say" : age;
    let genderSelection = gender === "-" ? "prefer not to say" : gender;
    let roleSelection = role === "-" ? "prefer not to say" : role;

    // Convert comma-separated emails → array
    const referFriendEmailsArray = referFriend
      ? referFriend
          .split(",")
          .map((e) => e.trim())
          .filter((e) => e !== "")
      : [];

    // Session payload
    const sessionPayload = {
      request: "setUserData",
      key: cookies["key"],
      firstName,
      lastName,
      age: ageSelection,
      gender: genderSelection,
      location,
      email,
      company,
      phone,
      jobTitle,
      interest,
      referFriend,
      role: roleSelection,
    };

    // Updated assessment user payload
    const assessmentUserPayload = {
      userId: cookies["key"],
      firstName,
      lastName,
      // DB-backed endpoint expects normalized allowed values
      age: normalizePearliAge(ageSelection),
      gender: normalizePearliGender(genderSelection),
      inputLocation: location,
      inputEmail: email,
      jobTitle,
      selfDescription: normalizePearliDescription(roleSelection),
      phone,
      referredFriendEmails: referFriendEmailsArray, // <-- UPDATED
    };

    // Parallel calls
    const [sessionRes, assessmentRes] = await Promise.allSettled([
      axios.post(`${API_URL}/session`, sessionPayload, headerConfig),
      axios.post(
        `${API_URL}/api/assessment-users/`,
        assessmentUserPayload,
        headerConfig
      ),
    ]);

    if (sessionRes.status === "fulfilled" && sessionRes.value.data?.persisted === true && assessmentRes.status === "fulfilled") {
      sessionStorage.setItem("pearliEmail", email);
      navigate("/PEARLIReports");
    } else if (sessionRes.status === "fulfilled" || assessmentRes.status === "fulfilled") {
      toast.warn("Some data may not have been saved completely, but continuing...");
      // navigate("/PEARLIReports");
    } else {
      toast.error("Could not complete your submission. Please try again.");
    }
  } catch (err) {
    toast.error("Something went wrong while saving your data. Please try again.");
  } finally {
    setIsLoading(false);
  }
};

function valid(firstName, lastName, age, gender, locationValue, email, referFriend, setErrors) {
  const newErrors = {};
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  if (!firstName.current.value.trim()) newErrors.firstName = "First name is required";
  if (!lastName.current.value.trim()) newErrors.lastName = "Last name is required";
  if (age.current.value === "-") newErrors.age = "Select your age";
  if (gender.current.value === "-") newErrors.gender = "Select your gender";
  if (!locationValue) newErrors.location = "Select your country";

  if (!emailRegex.test(email.current.value))
    newErrors.email = "Invalid email address";

  if (referFriend.current.value) {
    const emails = referFriend.current.value.split(",");
    const invalids = emails.filter(
      (e) => e.trim() && !emailRegex.test(e.trim())
    );
    if (invalids.length > 0) newErrors.referFriend = "Invalid friend email(s)";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
}

export const UserData = () => {
  const [cookies] = useCookies(["sessionLog"]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("");

  const firstName = useRef();
  const lastName = useRef();
  const age = useRef();
  const gender = useRef();
  // location is now controlled via selectedCountry to support flags in dropdown
  const email = useRef();
  const company = useRef();
  const phone = useRef();
  const jobTitle = useRef();
  const interest = useRef();
  const referFriend = useRef();
  const role = useRef();
  const navigate = useNavigate();

  const countryList = useMemo(() => {
    return buildCountryList(countries);
  }, []);

  return (
    <div className="userDataInput">
      <Header
        title="Please input your data"
        subtitle="Afterwards you will be shown your report"
      />
      <link href="https://fonts.googleapis.com/css?family=Inter" rel="stylesheet" />
      <div className="MainInput">
        <h1 className="dataTitle topText">
          Congratulations on completing your well-being assessment! To view your results,
          please fill out the following information.
        </h1>
        <h1 className="dataSubtitle">
          A copy of the PEARLI assessment results will be sent to the email address you provide.
        </h1>

        {/* First Name */}
        <div className="dataInputLabel">First Name *</div>
        <div className={`dataInputBlock ${errors.firstName ? "error" : ""}`}>
          <input placeholder="First Name" ref={firstName} />
        </div>
        {errors.firstName && <div className="fieldError">{errors.firstName}</div>}

        {/* Last Name */}
        <div className="dataInputLabel">Last Name *</div>
        <div className={`dataInputBlock ${errors.lastName ? "error" : ""}`}>
          <input placeholder="Last Name" ref={lastName} />
        </div>
        {errors.lastName && <div className="fieldError">{errors.lastName}</div>}

        {/* Age */}
        <div className="dataInputLabel">Age *</div>
        <div className={`dataInputBlock ${errors.age ? "error" : ""}`}>
          <select ref={age} defaultValue="-">
            <option value="-">-</option>
            <option value="under 18">Under 18</option>
            <option value="18-24">18-24</option>
            <option value="25-29">25-29</option>
            <option value="30-34">30-34</option>
            <option value="35-44">35-44</option>
            <option value="45-55">45-55</option>
            <option value="55+">55+</option>
            <option value="prefer not to say">Prefer not to say</option>
          </select>
        </div>
        {errors.age && <div className="fieldError">{errors.age}</div>}

        {/* Gender */}
        <div className="dataInputLabel">Gender *</div>
        <div className={`dataInputBlock ${errors.gender ? "error" : ""}`}>
          <select ref={gender} defaultValue="-">
            <option value="-">-</option>
            <option value="Male">👨 Male</option>
            <option value="Female">👩 Female</option>
            <option value="Non-Binary">🌈 Non-Binary</option>
            <option value="Other">👤 Other</option>
            <option value="Prefer Not to Say">🙅 Prefer Not to Say</option>
          </select>
        </div>
        {errors.gender && <div className="fieldError">{errors.gender}</div>}

        {/* Location */}
        <div className="dataInputLabel">Location *</div>
        <div className={`dataInputBlock ${errors.location ? "error" : ""}`}>
          <TextField
            select
            size="small"
            fullWidth
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              if (errors.location) {
                setErrors((prev) => ({ ...prev, location: undefined }));
              }
            }}
          >
            <MenuItem value="">-</MenuItem>
            {countryList.map((country) => (
              <MenuItem key={country.code} value={country.name}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ReactCountryFlag
                    countryCode={country.code}
                    svg
                    style={{ width: "1.5em", height: "1.5em" }}
                  />
                  {country.name}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </div>
        {errors.location && <div className="fieldError">{errors.location}</div>}

        {/* Email */}
        <div className="dataInputLabel">Email Address *</div>
        <div className={`dataInputBlock ${errors.email ? "error" : ""}`}>
          <input placeholder="Email" ref={email} />
        </div>
        {errors.email && <div className="fieldError">{errors.email}</div>}

        {/* Company Name */}
        <div className="dataInputLabel">Company Name (Optional)</div>
        <div className="dataInputBlock">
          <input placeholder="Company Name" ref={company} />
        </div>

        {/* Phone */}
        <div className="dataInputLabel">Phone Number (Optional)</div>
        <div className="dataInputBlock">
          <input placeholder="Phone Number" ref={phone} />
        </div>

        {/* Job Title */}
        <div className="dataInputLabel">Job Title (Optional)</div>
        <div className="dataInputBlock">
          <input placeholder="Job Title" ref={jobTitle} />
        </div>

        {/* Interest */}
        <div className="dataInputLabel">
          Tell us more about your interest in Mindsherpa Health (Optional)
        </div>
        <div className="dataInputBlock">
          <textarea placeholder="Share your interests here..." ref={interest} rows={4} />
        </div>

        {/* Refer a Friend */}
        <div className="dataInputLabel">
          Refer a Friend (Email, multiple allowed separated by commas)
        </div>
        <div className={`dataInputBlock ${errors.referFriend ? "error" : ""}`}>
          <input
            placeholder="friend1@example.com, friend2@example.com"
            ref={referFriend}
          />
        </div>
        {errors.referFriend && <div className="fieldError">{errors.referFriend}</div>}

        <hr />

        {/* Role */}
        <div className="dataInputLabel">Best Description Of Yourself:</div>
        <div className="dataInputBlock">
          <select ref={role} defaultValue="-">
            <option value="-">-</option>
            <option value="Student">Student</option>
            <option value="Working Professional">Working Professional</option>
            <option value="Executive / Leadership">Executive / Leadership</option>
            <option value="Caregiver / Parent">Caregiver / Parent</option>
            <option value="Entrepreneur">Entrepreneur</option>
            <option value="Retired">Retired</option>
            <option value="Athlete">Athlete</option>
            <option value="Prefer Not to Say">Prefer Not to Say</option>
          </select>
        </div>

        {/* Submit */}
        <button
          className="finishButton"
          style={{
            backgroundColor: "#2E8B57",
            color: "white",
            fontSize: "1.2em",
            padding: "12px 24px",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.6 : 1,
          }}
          disabled={isLoading}
          onClick={() => {
            if (
              valid(
                firstName,
                lastName,
                age,
                gender,
                selectedCountry,
                email,
                referFriend,
                setErrors
              )
            ) {
              handleClick(
                cookies,
                navigate,
                setIsLoading,
                firstName.current.value,
                lastName.current.value,
                age.current.value,
                gender.current.value,
                selectedCountry,
                email.current.value,
                company.current.value,
                phone.current.value,
                jobTitle.current.value,
                interest.current.value,
                referFriend.current.value,
                role.current.value
              );
            }
          }}
        >
          {isLoading
            ? "Directing to PEARLI™ Reports..."
            : "View PEARLI™ Assessment Results"}
        </button>
      </div>
    </div>
  );
};

export default UserData;
